/**
 * Types pour l'application Inbasket
 * Alignés avec le schema Convex (pattern identique à form)
 */

import type { ReactElement } from "react";
import type { Doc } from "../../../../convex/_generated/dataModel";
import {
  StatutDetailInbasketEnum,
  StatutDocumentEnum,
  StatutGlobalEnum,
  StatutMailEnum,
  TemplateInbasketEnum,
  TypeEvalEnum,
} from "../../../../convex/schema";

// Re-export enums pour utilisation dans le frontend
export {
  StatutDetailInbasketEnum,
  StatutDocumentEnum,
  StatutGlobalEnum,
  StatutMailEnum,
  TemplateInbasketEnum,
  TypeEvalEnum
};

// Alias pour compatibilité avec les composants existants
export const MailStatus = StatutMailEnum;

// ============================================
// TYPES DE BASE (pattern identique à form)
// ============================================

export type Evaluation = Doc<"evaluations">;

// Embedded types from schema (not tables, so not in Doc<>)
export type DonneesInbasket = Extract<
  Evaluation["donnees"],
  { type: "inbasket" }
>;
export type InbasketEvaluation = Omit<Evaluation, "donnees"> & {
  donnees: DonneesInbasket;
};

// ============================================
// TYPES SPÉCIFIQUES INBASKET
// ============================================

// Réponses aux mails (structure dans donnees.reponses)
export type MailReponses = DonneesInbasket["reponses"];
export type MailReponse = MailReponses[string];

// Statuts des documents (structure dans donnees.statutsDocuments)
export type StatutsDocuments = DonneesInbasket["statutsDocuments"];

// ============================================
// TYPES POUR LES COMPOSANTS MAIL
// ============================================

/**
 * Type Mail utilisé par les composants (compatible avec mail-data.ts)
 */
export interface Mail {
  _id: string;
  cleMail: string;
  titre: string;
  emetteur: string;
  emetteurPoste: string;
  emetteurEntreprise: string;
  emetteurLogo: string | null;
  dateRelative: number;
  piecesJointes: string[];
  messageTransfert: boolean;
  message?: string;
  messagesTransfert?: string[];
}

/**
 * Type PieceJointe pour les pièces jointes des mails
 */
export interface PieceJointe {
  cle: string;
  titre: string;
}

/**
 * Type Document pour les documents de la sidebar
 */
export interface Document {
  _id: string;
  titre: string;
  contenu: string;
}

// Étapes ordonnées pour la progression de l'évaluation
export const orderedEvaluationSteps: StatutDetailInbasketEnum[] = [
  StatutDetailInbasketEnum.NON_COMMENCEE,
  StatutDetailInbasketEnum.LECTURE_INFORMATIONS_CONTEXTE,
  StatutDetailInbasketEnum.LECTURE_MAILS,
  StatutDetailInbasketEnum.REPONSE_MAILS,
  StatutDetailInbasketEnum.PRIORISATION_MAILS,
  StatutDetailInbasketEnum.MAIL_PRESIDENTE,
  StatutDetailInbasketEnum.FINIE,
  StatutDetailInbasketEnum.INCOMPLETE,
];

// ============================================
// GESTION D'ERREURS (identique à form)
// ============================================

/**
 * Type pour les réponses d'erreur du backend
 */
export interface BackendError {
  erreur: string;
  bloqueJusquA?: number;
}

/**
 * Résultat d'une query/mutation qui peut échouer
 */
export type BackendResult<T> = T | BackendError;

/**
 * Type guard pour vérifier si c'est une erreur
 */
export function isBackendError(result: unknown): result is BackendError {
  return (
    typeof result === "object" &&
    result !== null &&
    "erreur" in result &&
    typeof (result as BackendError).erreur === "string"
  );
}

// Props passés aux composants de contenu mail
export interface MailContentProps {
  variablesData: VariablesData;
}

// Paramètres passés à la fonction displayCondition d'un mail
export interface DisplayConditionParams {
  reponses: MailReponses;
  templateMails: MailComponent[];
  mailTypeReponses: Record<string, import("../templates/types").TypeReponseMail>;
}

// Fonction conditionnant l'affichage d'un mail (true = visible, false = caché)
export type DisplayCondition = (params: DisplayConditionParams) => boolean;

// Type pour un composant mail avec metadata statique
export interface MailComponent {
  (props: MailContentProps): ReactElement | null;
  metadata: MailMetadata;
  displayCondition?: DisplayCondition;
}

// Types pour les métadonnées des mails
export interface MailMetadata {
  titre: string;
  cleMail: string;
  messageTransfert: boolean;
  emetteur: string;
  emetteurPoste: string;
  emetteurEntreprise: string;
  emetteurLogo: string | null;
  dateRelative: number;
  piecesJointes: string[];
  // Propriétés optionnelles pour compatibilité avec les composants
  message?: string;
  messagesTransfert?: string[];
}

// Type pour les variables de données (templates)
export interface VariablesData {
  genre_masc_abrege?: string;
  genre_masc_complet?: string;
  responsable_grand_est_precedent_nom?: string;
  employe_service_du_personnel_nom?: string;
  collaborateur_1_prenom?: string;
  collaborateur_1_nom?: string;
  collaborateur_1_fonction?: string;
  collaborateur_1_societe?: string;
  [key: string]: string | undefined;
}

export interface PieceJointeContentProps {
  variablesData: VariablesData;
}

// Type pour un composant mail avec metadata statique
export interface PieceJointeComponent {
  (props: PieceJointeContentProps): ReactElement | null;
  title: string;
}

// ============================================
// TYPES POUR LES COMPOSANTS DE DOCUMENTS
// ============================================

/**
 * Métadonnées pour les documents de la sidebar
 */
export interface DocumentMetadata {
  id: string;
  titre: string;
  icon: string;
}

/**
 * Props pour les composants de documents
 */
export interface DocumentProps {
  variablesData: VariablesData;
}

/**
 * Type DocumentComponent analogue à MailComponent
 * Utilisé pour les documents affichés dans la sidebar
 */
export interface DocumentComponent {
  (props: DocumentProps): ReactElement | null;
  metadata: DocumentMetadata;
}
